import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faLink, faCheck, faBars } from '@fortawesome/pro-regular-svg-icons';
import cn from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import { confirmAlert } from 'react-confirm-alert';
import './ShopCollectionResult.scss';

import { getPinsForCuratorCollection } from '../../../APIClient/shop';

import { dontBubble, copyToClipboard } from '../../../Helpers/helpers';
import { getUserId, isShopper, getSections } from '../../../Helpers/user_helpers';
import { getPrettyTimeAgoFromNow, getPrettyNumber } from '../../../Helpers/formatting';
import { getLinkToShopForUsername, getActiveCuratorGroup } from '../../../Helpers/shop_helpers';
import { isAnalyticsMode } from '../../../Helpers/ui_helpers';

import SmartImage from '../../General/SmartImage';
import Tooltip from '../../General/Tooltip';
import ShopCollectionActionsButton from './ShopCollectionActionsButton';

const ShopCollectionResult = props => {
  const { previewType, collection, ui, currentAnalytics } = props;
  const { preview_pins, total_num_pins, isArchived } = collection;
  const isHidden = !!collection.private;

  // Analytics
  const areAnalyticsVisible = isAnalyticsMode(ui);
  const numViews = currentAnalytics?.collection_stats?.[collection.id]?.num_views || 0;
  const isMyCollection = collection.User_id === getUserId(props.user);

  const linkToCollection = `/shop/collections/${collection.id}`;
  const linkToCuratorShop = getLinkToShopForUsername(collection.User_username, { tab: 'collections' });
  const showCollectionUserName = !!getActiveCuratorGroup(props.shop)?.id;

  // UI Types
  const isPost = ['post', 'post_vertical', 'post_horizontal', 'posts'].includes(previewType);
  const isShelf = ['shelf', 'large_preview', 'quick-shop', 'shelves', 'gift_guides'].includes(previewType);
  const isEditorial = ['editorial'].includes(previewType);

  // Allow them to show more pins
  const [fetchingAllPins, setFetchingAllPins] = React.useState(false);
  const [allPins, setAllPins] = React.useState([]);
  const [numVisiblePins, setNumVisiblePins] = React.useState(8);
  const hasMorePins = total_num_pins > numVisiblePins;
  const showMorePins = async () => {
    if (fetchingAllPins) return;
    !allPins.length && (await fetchAllPins());
    setNumVisiblePins(numVisiblePins + 12);
  };

  // Decide visible pins
  const visiblePins = allPins.length
    ? allPins.slice(0, collection.image ? numVisiblePins - 1 : numVisiblePins)
    : preview_pins.slice(0, collection.image ? numVisiblePins - 1 : numVisiblePins);

  const fetchAllPins = async () => {
    setFetchingAllPins(true);
    try {
      const resp = await getPinsForCuratorCollection(collection.id);
      setAllPins(resp.pins);
    } catch (e) {
      console.error(e);
    }
    setFetchingAllPins(false);
  };

  // Smooth Animation of Images
  const [postImageLoaded, setPostImageLoaded] = React.useState(false);
  const onPostImageLoad = () => {
    setTimeout(() => setPostImageLoaded(true), 100);
  };

  // Actions
  const showCollection = e => {
    e.preventDefault();
    e.stopPropagation();
    confirmAlert({
      title: 'Are you sure?',
      message: 'This collection is currently hidden. Would you like to show it?',
      buttons: [
        { label: 'Cancel', className: 'cancel', onClick: () => {} },
        {
          label: 'Make Public',
          onClick: () => {
            props.editCollection(collection.id, { private: false });
            props.setCollectionResults(props.collectionResults.map(c => (c.id === collection.id ? { ...c, private: false } : c)));
          }
        }
      ]
    });
  };

  const unarchiveCollection = e => {
    e.preventDefault();
    e.stopPropagation();
    confirmAlert({
      title: 'Are you sure?',
      message: 'This collection is currently archived. Would you like to unarchive it?',
      buttons: [
        { label: 'Cancel', className: 'cancel', onClick: () => {} },
        {
          label: 'Unarchive',
          onClick: () => {
            props.editCollection(collection.id, { isArchived: false });
            props.setCollectionResults(props.collectionResults.filter(c => c.id !== collection.id));
            const section = getSections(props.user).find(s => s.id === collection.Section_id);
            window.ALERT.success(`Collection has been moved back to its original section "${section?.title}".`);
          }
        }
      ]
    });
  };

  // Actions
  const showActionButtons = isMyCollection && !isShopper(props.user);
  const [isCopying, setIsCopying] = React.useState(false);
  const copyLink = () => {
    setIsCopying(true);
    copyToClipboard(window.location.origin + linkToCollection);
    setTimeout(() => setIsCopying(false), 1000);
  };
  const actionButtons = showActionButtons ? (
    <div
      onClick={dontBubble}
      className={cn('shop-collection-result-actions-container actions-container', {
        'on-dark': isPost
      })}
    >
      {props.rearrangeHandle && (
        <Tooltip message='Rearrange'>
          <div className='action'>{props.rearrangeHandle}</div>
        </Tooltip>
      )}
      <Tooltip forceOpen={isCopying} message={isCopying ? 'Copied link to Clipboard!' : 'Share'}>
        <div onClick={copyLink} className={cn('action', { copying: isCopying })}>
          <FontAwesomeIcon className='icon' icon={isCopying ? faCheck : faLink} />
        </div>
      </Tooltip>
      <Tooltip message='More Options'>
        <div className='action'>
          <ShopCollectionActionsButton
            collection={collection}
            user={props.user}
            editCollection={props.editCollection}
            deleteCollection={props.deleteCollection}
            collectionResults={props.collectionResults}
            setCollectionResults={props.setCollectionResults}
          />
        </div>
      </Tooltip>
    </div>
  ) : null;

  const viewsBadge = areAnalyticsVisible ? (
    <span className='views-count'>{props.isFetchingAnalytics ? '...' : `${getPrettyNumber(numViews)} view${numViews !== 1 ? 's' : ''}`}</span>
  ) : null;

  // Get metadata
  let metadata = [];
  if (collection.User_name && showCollectionUserName) metadata.push(collection.User_name);
  metadata.push(`${total_num_pins} Product${total_num_pins !== 1 ? 's' : ''}`);

  const updatedThisWeek = moment(collection.lastUpdated).isAfter(moment().subtract(7, 'days'));
  updatedThisWeek && metadata.push(`Updated ${getPrettyTimeAgoFromNow(collection.lastUpdated, { longForm: true })}`);

  if (isHidden) metadata.push('Currently Hidden');
  if (isArchived) metadata.push('Currently Archived');

  // UI Handling
  const previewImage = collection.image || collection.preview_pins[0]?.image;
  const additionalClasses = cn(`column-${props.columnIdx}`, {
    'image-loaded': postImageLoaded,
    'has-image': !!previewImage,
    hidden: isHidden,
    archived: isArchived,
    'hidden-or-archived': isHidden || isArchived
  });
  const isHiddenOrArchived = !!isHidden || !!isArchived;
  const hiddenOrArchivedOverlay = isHiddenOrArchived && (
    <div onClick={isArchived ? unarchiveCollection : showCollection} className='hidden-or-archived-overlay'>
      <div className='overlay-badge'>{isArchived ? 'Currently Archived' : 'Currently Hidden'}</div>
    </div>
  );

  if (isPost) {
    return (
      <div className={cn('shop-collection-result post', collection.Section_previewType, additionalClasses)}>
        {viewsBadge}
        <Link to={`/shop/collections/${collection.id}`}>
          <>
            <div to={`/shop/collections/${collection.id}`} className={cn('image-container', additionalClasses)}>
              {previewImage ? (
                <img onLoad={onPostImageLoad} className={cn('cover-img', additionalClasses)} src={previewImage} alt={collection.name} />
              ) : (
                <div className='empty-pin-image' />
              )}
            </div>
            <div className={cn('data-container', additionalClasses)}>
              {showCollectionUserName && <div className='title'>{collection.User_name}</div>}
              <h4 className='subtitle'>{getPrettyTimeAgoFromNow(collection.createdAt, { longForm: true })}</h4>
            </div>
          </>
          {hiddenOrArchivedOverlay}
        </Link>
        {actionButtons}
      </div>
    );
  }

  if (isShelf) {
    const additionalClasses = { 'hidden-or-archived': isHidden || isArchived };
    return (
      <Link to={linkToCollection} className={cn('shop-collection-result shelf', additionalClasses)}>
        {viewsBadge}
        <div className={cn('preview-pins', additionalClasses)}>
          {visiblePins.length
            ? visiblePins.slice(0, 5).map((pin, idx) => {
                const fadeInDelay = idx * 100 + (props.rowIdx <= 3 ? props.rowIdx * 25 : 0);
                return (
                  <div key={pin.id} className='preview-pin'>
                    <div className='image-container'>
                      <SmartImage growIn fadeInDelay={fadeInDelay} src={pin.image} alt={pin.name} />
                    </div>
                  </div>
                );
              })
            : _.range(5).map(idx => (
                <div key={idx} className='preview-pin'>
                  <div className='image-container'>
                    <div className='empty-pin-image' />
                  </div>
                </div>
              ))}
          {hiddenOrArchivedOverlay}
        </div>
        <div className='collection-result-body-container'>
          <div className='result-data'>
            {showCollectionUserName && collection.User_image && !isMyCollection && (
              <Link to={linkToCuratorShop} className='image-container'>
                <img src={collection.User_image} alt={collection.User_name} />
              </Link>
            )}
            <div className='metadata-container'>
              <h3 className={cn('title', additionalClasses)}>
                <span>{collection.name || 'Untitled Collection'}</span>
                {areAnalyticsVisible ? <>{`  `}</> : null}
              </h3>
              <h4 className={cn('subtitle', additionalClasses)}>{metadata.join(' • ')}</h4>
            </div>
          </div>
          {showActionButtons ? (
            actionButtons
          ) : (
            <div className='go-to-collection'>
              View Full Collection
              <FontAwesomeIcon icon={faChevronRight} />
            </div>
          )}
        </div>
      </Link>
    );
  }

  if (isEditorial) {
    const primaryImage = collection.image || collection.preview_pins[0]?.image;
    const additionalClasses = { 'hidden-or-archived': isHidden || isArchived };
    return (
      <Link to={linkToCollection} className={cn('shop-collection-result editorial', additionalClasses)}>
        {viewsBadge}
        <div className={cn('image-container-outer', additionalClasses)}>
          <div className={cn('image-container', additionalClasses)}>
            {primaryImage ? (
              <SmartImage
                growIn
                onLoad={onPostImageLoad}
                fadeInDelay={props.rowIdx <= 3 ? props.rowIdx * 100 : 0}
                src={primaryImage}
                alt='Editorial'
              />
            ) : (
              <div className='empty-pin-image' />
            )}
            {hiddenOrArchivedOverlay}
          </div>
        </div>
        <div className='data-container'>
          <h3 className={cn('title', additionalClasses)}>
            <span>{collection.name || 'Untitled Collection'}</span>
          </h3>
          <h4 className={cn('subtitle', additionalClasses)}>{metadata.join(' • ')}</h4>
          <div className={cn('view-collection', additionalClasses)}>
            View Collection
            <FontAwesomeIcon icon={faChevronRight} />
          </div>
        </div>
        {actionButtons}
      </Link>
    );
  }

  return (
    <div className='shop-collection-preview-container'>
      <h1>Unknown Type: {previewType}</h1>
    </div>
  );
};

ShopCollectionResult.propTypes = {
  ui: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  shop: PropTypes.object.isRequired,
  collection: PropTypes.object.isRequired,
  columnIdx: PropTypes.number.isRequired,
  rowIdx: PropTypes.number.isRequired,
  previewType: PropTypes.string.isRequired,
  editCollection: PropTypes.func.isRequired,
  deleteCollection: PropTypes.func.isRequired,
  collectionResults: PropTypes.array.isRequired,
  setCollectionResults: PropTypes.func.isRequired
};

export default ShopCollectionResult;
